import type { TQuestionAnswer } from '$/types/components/questions';
import type { IAutocompleteProps, IGraphqlResponse } from './autocomplete';
import type { GraphQLResult } from '@aws-amplify/api-graphql';

import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API } from 'aws-amplify';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import ErrorMessage from '../ErrorMessage';
import TextInput from '../Inputs/TextInput';
import Loader from '../Loader';
import SelectAnswer from '../SelectAnswer';

import CenteredDiv from './CenteredDiv';

import theme from '$/assets/theme';
import ErrorMessages from '$/types/enums/ErrorMessages';

const __MAX_ELEMENTS__ = 5;

const Wrapper = styled.div`
  margin: 0 auto;
`;

const handleSortByLength = (a, b) => {
  return a.value?.length - b.value?.length;
};

const Autocomplete = React.forwardRef<HTMLInputElement, IAutocompleteProps>(
  (props, ref) => {
    const [buttonFocusIndex, setButtonFocusIndex] = useState(0);
    const [init, setInit] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const maxElements = props.maxElements || __MAX_ELEMENTS__;

    useDebounce(
      () => {
        setDebouncedSearchTerm(searchTerm);
      },
      500,
      [searchTerm]
    );

    // replace search with value, initial value is not a value from state
    useEffect(() => {
      if (props.replaceSearch && props.value?.[0]?.name) {
        setSearchTerm(props.value[0].name);
      }
    }, [props.value, props.replaceSearch]);

    const getDataQuery = useQuery(
      ['autocomplete', props.query, debouncedSearchTerm],
      async () => {
        const a = (await API.graphql({
          query: props.query,
          variables: {
            query: debouncedSearchTerm || props.defaultSearch || 'A',
          },
          authMode: GRAPHQL_AUTH_MODE.API_KEY,
        })) as GraphQLResult<IGraphqlResponse>;
        return a?.data;
      },
      {
        enabled: !!(props.init || debouncedSearchTerm || props.defaultSearch),
        onSuccess: () => setInit(true),
      }
    );

    const handleUpdateSearchTerm = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.currentTarget.value);
        props.onChange([]);
        setButtonFocusIndex(0);
      },
      []
    );

    const handleOnChange = useCallback(
      (data: TQuestionAnswer[]) => {
        if (data.length > 0) {
          setSearchTerm(data[0].value);
        }
        if (props.onValueSelect) {
          props.onValueSelect(data);
        }
        props.onChange(data);
      },
      [props.onChange]
    );

    const resetSearch = useCallback(() => {
      setSearchTerm('');
      props.onChange([]);
    }, []);

    const onWrapperKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') e.preventDefault();
      if (e.key === 'ArrowDown' || e.key === 'Tab') {
        setButtonFocusIndex((prevState) => {
          if (prevState === displayList.length) {
            return prevState;
          }
          return prevState + 1;
        });
      } else if (e.key === 'ArrowUp') {
        setButtonFocusIndex((prevState) => {
          if (prevState === 0) {
            return prevState;
          }
          if (prevState === 1) {
            ref.current.focus();
          }
          return prevState - 1;
        });
      }
    };

    const displayList = useMemo((): TQuestionAnswer[] => {
      if (!getDataQuery.data) {
        return [];
      }

      let tempList = [
        ...Object.values(getDataQuery.data)[0]
          .sort(handleSortByLength)
          .slice(0, maxElements),
      ];

      if (
        props.allowCustomAnswer &&
        searchTerm.length >= 2 &&
        !tempList.find(
          (i) => i.value.toLowerCase() === searchTerm.toLowerCase()
        )
      ) {
        tempList = [
          ...tempList.slice(0, maxElements),
          {
            id: searchTerm,
            name: searchTerm,
            value: searchTerm,
          },
        ];
      }

      if (
        props.value.length > 0 &&
        !tempList.some((i) => i.id == props.value[0]?.id) &&
        props.showValue
      ) {
        tempList = [...tempList, ...props.value];
      }
      return tempList;
    }, [searchTerm, getDataQuery.data, props.value]);

    return (
      <Wrapper {...props.wrapperProps} onKeyDown={onWrapperKeyDown}>
        <TextInput
          ref={ref}
          name={props.name}
          placeholder={props.placeholder}
          value={searchTerm}
          onChange={handleUpdateSearchTerm}
          iconPosition={searchTerm ? 'end' : 'start'}
          disabled={props.disabled}
          autoComplete="off"
          icon={
            searchTerm ? (
              <FontAwesomeIcon
                icon={faTimesCircle}
                onClick={resetSearch}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )
          }
          containerProps={{
            style: {
              margin: '8px 0',
              borderColor: props.invalid && theme.colors.functionalRed,
            },
          }}
        />
        {!init && getDataQuery.isLoading ? (
          <CenteredDiv>
            <Loader width="35px" height="35px" />
          </CenteredDiv>
        ) : (
          <SelectAnswer
            list={
              ((searchTerm || props.defaultSearch) && displayList) ||
              props.value ||
              []
            }
            fieldProps={{
              name: props.name,
              value: props.value,
              onChange: handleOnChange,
            }}
            maxElements={1}
            collapse={props.collapse}
            hide={props.replaceSearch && props.value?.[0]?.value === searchTerm}
            boldedText={searchTerm}
            errorMessage={props.errorMessage}
            buttonProps={props.buttonProps}
            buttonFocusIndex={buttonFocusIndex}
          />
        )}
        {getDataQuery.isSuccess &&
          displayList?.length === 0 &&
          searchTerm.length > 0 && (
            <ErrorMessage>{ErrorMessages.NO_RESULT_FOR_QUERY}</ErrorMessage>
          )}
      </Wrapper>
    );
  }
);

Autocomplete.defaultProps = {
  allowCustomAnswer: true,
};

export default Autocomplete;
